(() => {
  'use strict';
  if (!customElements.get('sos-homepage-story')) {
    customElements.define('sos-homepage-story', class extends HTMLElement {
      connectedCallback() {
        if (this.dispose) return;
        const video = this.querySelector('[data-sos-video]');
        const play = this.querySelector('[data-sos-play]');
        const controls = this.querySelector('.film-controls');
        const error = this.querySelector('[data-sos-error]');
        if (!video || !play) return;
        const mobile = matchMedia('(max-width: 760px)');
        const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
        const sources = Array.from(video.querySelectorAll('source')).map(source => source.dataset.src || source.getAttribute('src'));
        const removers = [];
        const listen = (target, type, handler) => {
          target.addEventListener(type, handler);
          removers.push(() => target.removeEventListener(type, handler));
        };
        let active = true;
        let wantsPlayback = !reducedMotion.matches;
        let inView = false;
        let loadedSource = '';
        let observer;
        video.muted = true;
        video.controls = false;
        controls.hidden = false;
        const update = () => {
          play.textContent = video.paused ? this.dataset.playLabel : this.dataset.pauseLabel;
          play.setAttribute('aria-label', video.paused ? this.dataset.playAria : this.dataset.pauseAria);
        };
        const sync = () => {
          if (!active) return;
          if (wantsPlayback && inView && !document.hidden) {
            const selected = sources[mobile.matches ? 0 : 1];
            if (loadedSource !== selected) {
              loadedSource = selected;
              video.src = selected;
            }
            video.play().catch(update);
          } else video.pause();
        };
        const source = () => {
          video.poster = mobile.matches ? video.dataset.posterMobile : video.dataset.posterDesktop;
          if (loadedSource && loadedSource !== sources[mobile.matches ? 0 : 1]) {
            video.pause();
            video.removeAttribute('src');
            video.load();
            loadedSource = '';
          }
          error.hidden = true;
          controls.hidden = false;
          sync();
        };
        listen(play, 'click', () => { wantsPlayback = video.paused; sync(); });
        listen(video, 'play', update);
        listen(video, 'pause', update);
        listen(video, 'error', () => { error.hidden = false; controls.hidden = true; });
        listen(document, 'visibilitychange', sync);
        listen(mobile, 'change', source);
        listen(reducedMotion, 'change', () => { wantsPlayback = !reducedMotion.matches; sync(); });
        if ('IntersectionObserver' in window) {
          observer = new IntersectionObserver(entries => { inView = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.1; sync(); }, { threshold: 0.1 });
          observer.observe(video);
        } else inView = true;
        this.dispose = () => {
          active = false;
          video.pause();
          video.removeAttribute('src');
          video.load();
          observer?.disconnect();
          removers.forEach(remove => remove());
          this.dispose = null;
        };
        source();
        update();
      }
      disconnectedCallback() { this.dispose?.(); }
    });
  }
  if (!customElements.get('sos-newsletter-background')) {
    customElements.define('sos-newsletter-background', class extends HTMLElement {
      connectedCallback() {
        if (this.dispose) return;
        const section = this.closest('.newsletter');
        const dust = this.querySelector('.newsletter-dirt');
        if (!section || !dust) return;
        const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
        const controller = new AbortController();
        const options = { passive: true, signal: controller.signal };
        let visible = true;
        let frame = 0;
        let pointerX = 0;
        let pointerY = 0;
        let observer;
        const render = () => {
          frame = 0;
          if (reducedMotion.matches) {
            dust.style.removeProperty('--dust-x');
            dust.style.removeProperty('--dust-y');
            return;
          }
          if (!visible) return;
          const rect = section.getBoundingClientRect();
          const progress = Math.max(-1, Math.min(1,
            (innerHeight / 2 - rect.top - rect.height / 2) /
            ((innerHeight + rect.height) / 2)));
          const travel = innerWidth <= 760 ? 22 : 34;
          dust.style.setProperty('--dust-x', `${(pointerX * 8).toFixed(2)}px`);
          dust.style.setProperty('--dust-y', `${(progress * travel + pointerY * 6).toFixed(2)}px`);
        };
        const schedule = () => { if (!frame) frame = requestAnimationFrame(render); };
        window.addEventListener('scroll', () => {
          if (visible && !reducedMotion.matches) schedule();
        }, options);
        window.addEventListener('resize', schedule, options);
        reducedMotion.addEventListener('change', schedule, options);
        section.addEventListener('pointermove', event => {
          if (event.pointerType !== 'mouse' || reducedMotion.matches) return;
          const rect = section.getBoundingClientRect();
          pointerX = (event.clientX - rect.left) / rect.width * 2 - 1;
          pointerY = (event.clientY - rect.top) / rect.height * 2 - 1;
          schedule();
        }, options);
        section.addEventListener('pointerleave', () => {
          pointerX = pointerY = 0;
          schedule();
        }, options);
        if ('IntersectionObserver' in window) {
          observer = new IntersectionObserver(entries => {
            visible = entries[0].isIntersecting;
            if (visible) schedule();
          });
          observer.observe(section);
        }
        this.dispose = () => {
          controller.abort();
          observer?.disconnect();
          cancelAnimationFrame(frame);
          this.dispose = null;
        };
        schedule();
      }
      disconnectedCallback() { this.dispose?.(); }
    });
  }
  if (!customElements.get('sos-vibe-footer')) {
    customElements.define('sos-vibe-footer', class extends HTMLElement {
      connectedCallback() {
        if (this.dispose) return;
        const mobile = matchMedia('(max-width: 760px)');
        const groups = Array.from(this.querySelectorAll('.footer-group'));
        const layout = () => groups.forEach(group => {
          group.open = !mobile.matches;
          const summary = group.querySelector('summary');
          if (mobile.matches) summary.removeAttribute('tabindex');
          else summary.setAttribute('tabindex', '-1');
        });
        const click = event => { if (!mobile.matches) event.preventDefault(); };
        groups.forEach(group => group.querySelector('summary').addEventListener('click', click));
        mobile.addEventListener('change', layout);
        this.dispose = () => {
          groups.forEach(group => group.querySelector('summary').removeEventListener('click', click));
          mobile.removeEventListener('change', layout);
          this.dispose = null;
        };
        layout();
      }
      disconnectedCallback() { this.dispose?.(); }
    });
  }
})();

